import { useEffect, useState } from "react"
import { Link2, Link2Off, Loader2, TriangleAlert } from "lucide-react"
import { useParticipantIds } from "@daily-co/daily-react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { collabStatus, COLLAB_STUCK_AFTER_MS, type CollabStatus } from "@/lib/collabStatus"
import { useLiveDoc } from "./LiveDocProvider"

const STATUS_ICON = {
  off: Link2Off,
  connecting: Loader2,
  stuck: TriangleAlert,
  shared: Link2,
} as const

// Whether the whiteboard and the notes are shared with the other person right
// now (#711). Sits in the classroom header next to the lesson clock.
export function CollabStatusChip() {
  const { t } = useTranslation()
  const { enabled, synced } = useLiveDoc()
  const peerPresent = useParticipantIds({ filter: "remote" }).length > 0
  const [peerSince, setPeerSince] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // Start the clock when the peer shows up with the exchange still pending.
  useEffect(() => {
    setPeerSince(peerPresent && !synced ? Date.now() : null)
  }, [peerPresent, synced])

  // Re-evaluate once the stuck delay has passed; nothing ticks while waiting.
  useEffect(() => {
    if (peerSince == null) return
    const id = window.setTimeout(() => setNow(Date.now()), COLLAB_STUCK_AFTER_MS + 50)
    return () => window.clearTimeout(id)
  }, [peerSince])

  const status: CollabStatus = collabStatus({
    enabled,
    synced,
    peerPresent,
    peerWaitedMs: peerSince == null ? 0 : now - peerSince,
  })
  const Icon = STATUS_ICON[status]

  return (
    <Badge
      variant={status === "stuck" ? "destructive" : "outline"}
      className="gap-1 text-xs"
      data-testid="collab-status"
      data-status={status}
      role="status"
      title={t(`liveSession.collab.${status}`)}
      aria-label={t(`liveSession.collab.${status}`)}
    >
      <Icon className={status === "connecting" ? "w-3 h-3 animate-spin" : "w-3 h-3"} />
      <span className="hidden md:inline">{t(`liveSession.collab.${status}`)}</span>
    </Badge>
  )
}
